import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import './billing.css';

// The billing screens and the gate of each mount. The shell's router maps
// each entry to <ProtectedRoute><LazyPage><Component/></LazyPage></ProtectedRoute>
// at its place in the route tree.

const InvoiceListPage = lazy(() => import('./pages/invoices/InvoiceListPage'));
const ExternalSalesPage = lazy(() => import('./pages/invoices/ExternalSalesPage'));
const InvoiceCreatePage = lazy(() => import('./pages/invoices/InvoiceCreatePage'));
const InvoiceVerificationQueuePage = lazy(() => import('./pages/invoices/InvoiceVerificationQueuePage'));
const McfSignaturesEnDoutePage = lazy(() => import('./pages/invoices/McfSignaturesEnDoutePage'));
const InvoiceDetailPage = lazy(() => import('./pages/invoices/InvoiceDetailPage'));
const POSPage = lazy(() => import('./pages/invoices/POSPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const DailyXReportPage = lazy(() => import('./pages/reports/DailyXReportPage'));
const ZReportPage = lazy(() => import('./pages/reports/ZReportPage'));
const ArticleReportPage = lazy(() => import('./pages/reports/ArticleReportPage'));
const FECExportPage = lazy(() => import('./pages/reports/FECExportPage'));
const DgiIncidentRegularisationPage = lazy(() => import('./pages/reports/DgiIncidentRegularisationPage'));
const ProvisionalRegularisationPage = lazy(() => import('./pages/reports/ProvisionalRegularisationPage'));
const OriginalReissueReportPage = lazy(() => import('./pages/reports/OriginalReissueReportPage'));

export interface BillingRoute {
  /** Path relative to its parent in the route tree. */
  path: string;
  permission?: string;
  anyPermission?: string[];
  /** The page, declared lazy above; each page takes its own props. */
  Component: LazyExoticComponent<ComponentType<any>>;
  /** Props given to the page at this mount. */
  props?: Record<string, unknown>;
  /** The parent route the entry is a child of; absent for a top-level route. */
  parent?: 'rapports';
}

export const BILLING_ROUTES: readonly BillingRoute[] = [
  { path: 'invoices', permission: 'invoices:read', Component: InvoiceListPage },
  // Sales of the former invoicing system, credited after the switchover (#185).
  { path: 'invoices/external-sales', permission: 'invoices:read', Component: ExternalSalesPage },
  // The same form issues a sale invoice (invoices:create) and, with
  // `?avoir=RRR`, a standalone RRR credit note (invoices:credit-note).
  { path: 'invoices/new', anyPermission: ['invoices:create', 'invoices:credit-note'], Component: InvoiceCreatePage },
  { path: 'invoices/verification', permission: 'invoices:verify', Component: InvoiceVerificationQueuePage },
  // Gated by the permission of the act (`mcf:arbitrer-signature`), not by
  // `invoices:read`: the screen is the act, with nothing to read for someone
  // who cannot decide.
  { path: 'invoices/signatures-en-doute', permission: 'mcf:arbitrer-signature', Component: McfSignaturesEnDoutePage },
  { path: 'invoices/:id', permission: 'invoices:read', Component: InvoiceDetailPage },
  { path: 'pos', permission: 'pos:access', Component: POSPage },
  { path: 'general', parent: 'rapports', permission: 'dashboard:view', Component: ReportsPage },
  { path: 'rapports-fiscaux', permission: 'reports:daily', Component: ReportsPage, props: { fiscalOnly: true } },
  { path: 'reports/x-daily', permission: 'reports:daily', Component: DailyXReportPage },
  { path: 'reports/z', permission: 'reports:close', Component: ZReportPage },
  { path: 'reports/articles', permission: 'reports:daily', Component: ArticleReportPage },
  { path: 'reports/fec', permission: 'reports:fec', Component: FECExportPage },
  { path: 'reports/factures-a-regulariser', permission: 'reports:sales', Component: DgiIncidentRegularisationPage },
  { path: 'reports/documents-a-regulariser', permission: 'reports:sales', Component: ProvisionalRegularisationPage },
  { path: 'reports/reeditions-originaux', permission: 'invoices:verify', Component: OriginalReissueReportPage },
];
